import {
  notificationStatus,
  type NotificationEnvironment,
  type NotificationStatus,
} from '../../domain/notifications/policy';

/**
 * The browser side of notifications: capability, permission, and the push
 * subscription.
 *
 * Nothing here sends anything. Delivering a notification needs a server holding
 * the VAPID private key; see docs/NOTIFICATIONS.md for what is still missing.
 */

/**
 * The public half of a VAPID key pair, set at build time.
 *
 * Absent until one is generated, and the subscription step is skipped while it
 * is - permission still works, so the UI is exercised, but there is nothing to
 * subscribe to yet.
 */
const VAPID_PUBLIC_KEY: string | undefined = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export function isPushConfigured(): boolean {
  return typeof VAPID_PUBLIC_KEY === 'string' && VAPID_PUBLIC_KEY.length > 0;
}

/** iPhone/iPad, including iPadOS pretending to be a Mac with a touch screen. */
function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Launched from the Home Screen rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's own, non-standard flag - the only one iOS sets.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function readEnvironment(): NotificationEnvironment {
  const hasNotificationApi = typeof window !== 'undefined' && 'Notification' in window;
  return {
    hasNotificationApi,
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    isIos: isIos(),
    isStandalone: isStandalone(),
    permission: hasNotificationApi ? Notification.permission : null,
  };
}

export function currentStatus(): NotificationStatus {
  return notificationStatus(readEnvironment());
}

/**
 * Asks the browser for permission.
 *
 * Must be called from a user gesture - iOS refuses otherwise, and Chrome will
 * eventually follow. The caller is a button click, which is why this is not
 * done automatically on load.
 */
export async function requestPermission(): Promise<NotificationStatus> {
  if (!('Notification' in window)) return currentStatus();
  try {
    await Notification.requestPermission();
  } catch (error) {
    console.error('Notification permission request failed:', error);
  }
  return currentStatus();
}

/**
 * The active service worker, or null if it does not turn up in time.
 *
 * `navigator.serviceWorker.ready` never rejects and never times out - if no
 * worker ever activates it simply hangs, which would leave a button spinning
 * with no explanation. Everything here needs the worker, so everything here
 * goes through this.
 */
async function readyRegistration(timeoutMs = 3000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

/** The shape a push server needs to reach this device. */
export interface PushSubscriptionPayload {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/**
 * URL-safe base64 to the bytes `applicationServerKey` wants.
 *
 * Backed by an explicit ArrayBuffer rather than `Uint8Array.from`, because the
 * latter is typed over ArrayBufferLike - which admits SharedArrayBuffer - and
 * BufferSource does not accept that.
 */
function decodeVapidKey(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function encodeKey(subscription: PushSubscription, name: 'p256dh' | 'auth'): string {
  const key = subscription.getKey(name);
  if (!key) return '';
  return btoa(String.fromCharCode(...new Uint8Array(key)));
}

/**
 * Subscribes this device to push, reusing an existing subscription if there is
 * one.
 *
 * Returns null when there is no VAPID key configured yet, which is not an error
 * - it is the current state of the world. Permission is still worth having: it
 * is the part that needs the person, and it survives until they revoke it.
 */
export async function subscribeToPush(): Promise<PushSubscriptionPayload | null> {
  if (!isPushConfigured()) return null;
  if (!('serviceWorker' in navigator)) return null;

  try {
    const registration = await readyRegistration();
    if (!registration) return null;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // Required: a push service will not deliver to an unauthenticated sender.
        userVisibleOnly: true,
        applicationServerKey: decodeVapidKey(VAPID_PUBLIC_KEY!),
      }));

    return {
      endpoint: subscription.endpoint,
      p256dh: encodeKey(subscription, 'p256dh'),
      auth: encodeKey(subscription, 'auth'),
    };
  } catch (error) {
    console.error('Push subscription failed:', error);
    return null;
  }
}

/** Drops this device's subscription. Permission itself is the browser's to revoke. */
export async function unsubscribeFromPush(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await readyRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return null;
    const { endpoint } = subscription;
    await subscription.unsubscribe();
    return endpoint;
  } catch (error) {
    console.error('Push unsubscribe failed:', error);
    return null;
  }
}
